import BaseApiService from './baseApiService';
import {
    DecisionSubsidyReportCommand,
    DecisionSubsidyReportResult,
    DownloadEventFileQuery,
    DownloadSubsidyReportDraftQuery,
    GetSubsidyEventJournalFilter,
    GetSubsidyEventJournalQuery,
    GetSubsidyReportsQuery,
    GetSubsidyReportsVM,
    Paginated,
    SignDecisionCommand,
    SignReportPackageCommand,
    SubsidyEventJournalVM,
    SubsidyReportCardVM,
    SubsidyReportCreateDraftCommand,
    SubsidyReportDraftDto
} from '../contracts';
import { RcFile } from 'antd/lib/upload';
import JSZip from 'jszip';

class SubsidyReportsApiService extends BaseApiService {
    loadSubsidyReports = (request: GetSubsidyReportsQuery) => {
        return this.post<Paginated<GetSubsidyReportsVM>, GetSubsidyReportsQuery>('subsidy-reports', request);
    };

    loadReportStatuses = () => {
        return this.get<{ code: string; name: string }[]>('subsidy-reports/statuses');
    };

    downloadReportFile = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/archive/download`).then((resp) => resp.data);
    };

    loadSubsidyRemainingLimits = (year: number) => {
        return this.get<{ limit1: number; limit2: number; limit3: number; limit4: number; limit5: number } | null>(
            `subsidy-reports/${year}/remaining-limits`
        );
    };

    checkReportExists = (parameters: { year: number; month: number }) => {
        return this.get<string | null>('subsidy-reports/exists', {
            params: { year: parameters.year, month: parameters.month }
        });
    };

    runReport = (parameters: { year: number; month: number }) => {
        return this.post<number>('subsidy-reports/reserve', { year: parameters.year, month: parameters.month });
    };

    loadReportDetails = (reportId: number) => {
        return this.get<SubsidyReportCardVM>(`subsidy-reports/${reportId}/card`);
    };

    downloadArchive = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/archive/download`).then((resp) => resp.data);
    };

    downloadDeclaration = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/declaration/download`).then((resp) => resp.data);
    };

    uploadDeclaration = (reportId: number, file: RcFile) => {
        const form = new FormData();
        form.append('file', file);
        return this.post(`subsidy-reports/${reportId}/declaration/upload`, form);
    };

    deleteDeclaration = (reportId: number) => {
        return this.delete(`subsidy-reports/${reportId}/declaration`);
    };

    downloadQuarterlyReport = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/quarterly-report/download`).then((resp) => resp.data);
    };

    uploadQuarterlyReport = (reportId: number, file: RcFile) => {
        const form = new FormData();
        form.append('file', file);
        return this.post(`subsidy-reports/${reportId}/quarterly-report/upload`, form);
    };

    deleteQuarterlyReport = (reportId: number) => {
        return this.delete(`subsidy-reports/${reportId}/quarterly-report`);
    };

    downloadFormedPaymentDecision = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/formed-payment-decision/download`).then(
            (resp) => resp.data
        );
    };

    downloadUploadedPaymentDecision = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/uploaded-payment-decision/download`).then(
            (resp) => resp.data
        );
    };

    uploadPaymentDecision = (reportId: number, file: RcFile) => {
        const form = new FormData();
        form.append('file', file);
        return this.post(`subsidy-reports/${reportId}/uploaded-payment-decision/upload`, form);
    };

    deleteUploadedPaymentDecision = (reportId: number) => {
        return this.delete(`subsidy-reports/${reportId}/uploaded-payment-decision`);
    };

    downloadReturnLetter = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/return-letter/download`).then((resp) => resp.data);
    };

    uploadReturnLetter = (reportId: number, file: RcFile) => {
        const form = new FormData();
        form.append('file', file);
        return this.post(`subsidy-reports/${reportId}/return-letter/upload`, form);
    };

    downloadFormedReturnDecision = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/formed-return-decision/download`).then(
            (resp) => resp.data
        );
    };

    downloadUploadedReturnDecision = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/uploaded-return-decision/download`).then(
            (resp) => resp.data
        );
    };

    uploadReturnDecision = (reportId: number, file: RcFile) => {
        const form = new FormData();
        form.append('file', file);
        return this.post(`subsidy-reports/${reportId}/uploaded-return-decision/upload`, form);
    };

    deleteUploadedReturnDecision = (reportId: number) => {
        return this.delete(`subsidy-reports/${reportId}/uploaded-return-decision`);
    };

    sendReportOnSignAc = (reportId: number) => {
        return this.post(`subsidy-reports/${reportId}/on-sign-ac`);
    };

    disbandReport = (reportId: number) => {
        return this.post(`subsidy-reports/${reportId}/disband`);
    };

    returnReportToAc = (reportId: number) => {
        return this.post(`subsidy-reports/${reportId}/return-to-ac`);
    };

    agreeReport = (reportId: number) => {
        return this.post(`subsidy-reports/${reportId}/agree`);
    };

    declineReport = (reportId: number, reasons: string[], comment: string) => {
        return this.post('subsidy-reports/decline', { reportId: reportId, declineReasonsCodes: reasons, comment });
    };

    acceptReportToPayment = (reportId: number) => {
        return this.post(`subsidy-reports/${reportId}/accept-to-payment`);
    };

    loadFilesToSign = (reportId: number) => {
        return this.downloadFilePost(`subsidy-reports/${reportId}/files-for-sign`)
            .then((resp) => JSZip.loadAsync(resp.data))
            .then((zip) => Object.values(zip.files));
    };

    signReportPackage = (request: SignReportPackageCommand) => {
        return this.post<void, SignReportPackageCommand>('subsidy-reports/sign-report-package', request);
    };

    rejectReportByTreasury = (reportId: number) => {
        return this.post(`subsidy-reports/${reportId}/rejected-treasury`);
    };

    markReportAsPaid = (reportId: number, paymentDate: string) => {
        return this.post('subsidy-reports/to-paid', { reportId, paymentOrderDate: paymentDate });
    };

    signReportDecision = (request: SignDecisionCommand) => {
        return this.post('subsidy-reports/sign-decision', request);
    };

    loadReportDecisionDetails = (reportId: number) => {
        return this.get<DecisionSubsidyReportResult>(`subsidy-reports/${reportId}/decision`);
    };

    saveReportDecision = (request: DecisionSubsidyReportCommand) => {
        return this.post('subsidy-reports/decision', request);
    };

    rerunReport = (reportId: number) => {
        return this.post(`subsidy-reports/${reportId}/recreate`);
    };

    loadEvents = (request: GetSubsidyEventJournalQuery) => {
        return this.post<Paginated<SubsidyEventJournalVM>, GetSubsidyEventJournalQuery>(
            'subsidy-reports/event-journal',
            request
        );
    };

    exportEvents = (filter: GetSubsidyEventJournalFilter) => {
        return this.downloadFilePost<GetSubsidyEventJournalFilter>(
            'subsidy-reports/event-journal/download',
            filter
        ).then((resp) => resp.data);
    };

    exportEventFile = (request: DownloadEventFileQuery) => {
        return this.downloadFilePost('subsidy-reports/event-journal-file/download', request);
    };

    loadDeclineReasons = () => {
        return this.get<{ code: string; name: string }[]>('subsidy-reports/decline-reasons');
    };

    runReportDraft = (month: number, year: number) => {
        return this.post<void, SubsidyReportCreateDraftCommand>('subsidy-reports/draft', { month, year });
    };

    loadReportDraftCurrentState = async () => {
        return this.get<SubsidyReportDraftDto>('subsidy-reports/draft');
    };

    downloadReportDraft = (id: number) => {
        return this.downloadFilePost<DownloadSubsidyReportDraftQuery>(`subsidy-reports/draft/download`, {
            longOperationId: id
        }).then((resp) => resp.data);
    };
}

export default new SubsidyReportsApiService();
